// Data seam: sphinx REST /api/sphinx/runners + /jobs (server/routes/sphinx/queue.ts).
import { sphinxFetch } from "@/lib/sphinx-api";
import { useSphinxQuery, withInvalidate } from "@/lib/use-sphinx-query";
import {
  JOB_STATES,
  isLiveJobState,
  type JobClass,
  type JobState,
  type RunnerSlots,
} from "@/shared/sphinx";

export const QUEUE_POLL_MS = 3_000;

export type SphinxRunner = {
  id: string;
  name: string;
  capabilities: string[];
  slots: RunnerSlots;
  connected: boolean;
  lastSeenAt: string | null;
  /** Jobs assigned/running on this runner right now. */
  inFlight: number;
};

/** A jobs row minus `spec_json` (it carries auth tokens), plus its joins. */
export type SphinxJob = {
  id: string;
  iterationId: string;
  state: JobState;
  class: JobClass;
  priority: number;
  requires: string[];
  assignedRunner: string | null;
  assignedRunnerName: string | null;
  leaseExpiresAt: string | null;
  cancelRequested: boolean;
  requeueCount: number;
  result: unknown | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  runId: string | null;
  suiteId: string | null;
  suiteName: string | null;
  runNote: string | null;
  /** Why a queued mac-class job is being held (ambiguous client / golden restore); absent when claimable. */
  blockedBy?: string;
  specSummary: {
    target: string | null;
    model: string | null;
    yarnEnv: string | null;
    yarnOrg: string | null;
    caseTitle: string | null;
  };
};

export type QueueOverview = { runners: SphinxRunner[]; jobs: SphinxJob[] };

const listRunners = () => sphinxFetch<SphinxRunner[]>("/runners");
const listJobs = (limit = 200) =>
  sphinxFetch<SphinxJob[]>(`/jobs?state=${JOB_STATES.join(",")}&limit=${limit}`);

const fetchOverview = async (): Promise<QueueOverview> => {
  const [runners, jobs] = await Promise.all([listRunners(), listJobs()]);
  return { runners, jobs };
};

export const hasLiveJobs = (jobs: SphinxJob[]) =>
  jobs.some((j) => isLiveJobState(j.state));

/** Runners + jobs in one fetch; polls every 3s while any job is non-terminal. */
export function useQueueOverview() {
  return useSphinxQuery<QueueOverview>("queue:overview", fetchOverview, {
    pollMs: (data) => (hasLiveJobs(data.jobs) ? QUEUE_POLL_MS : null),
  });
}

export const setJobPriority = withInvalidate((id: string, priority: number) =>
  sphinxFetch<SphinxJob>(`/jobs/${encodeURIComponent(id)}/priority`, {
    method: "POST",
    body: { priority },
  })
);

export const cancelJob = withInvalidate((id: string) =>
  sphinxFetch<SphinxJob>(`/jobs/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
    body: {},
  })
);
